/* eslint-disable camelcase */

import { useTranslation } from 'react-i18next';

import { DefaultWidgetNames, WidgetSlotConfiguration, WidgetType } from '@/api/rest/resources/types/widget';
import { useDashboardOrganizationSummary } from '@/hooks/useDashboardOrganizationSummary';
import { useDisplayNumber } from '@/hooks/useDisplayNumber';
import { isNullish } from '@/utils/booleans';
import { squareMetersToHectares } from '@/utils/plot';

import { useFormattedCurrency } from '../m/Currency/Currency';

export const useTotalAccountValue = () => {
  const { t } = useTranslation();
  const {
    total_size: totalSize,
    total_stewarded_value_total: stewardedValueData,
    total_stewarded_value_per_ha: stewardedValuePerHaData,
  } = useDashboardOrganizationSummary().data ?? {};

  const { qtd_change } = stewardedValueData ?? {};
  const formattedStewardedValue = useFormattedCurrency(stewardedValueData?.value, stewardedValueData?.currency, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });

  const formattedAreaValue = useDisplayNumber(squareMetersToHectares(totalSize ?? 0), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });

  const formattedStewardedValuePerHa = useFormattedCurrency(
    stewardedValuePerHaData?.value,
    stewardedValuePerHaData?.currency,
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    },
  );

  return {
    name: DefaultWidgetNames.default_stewarded_value_card,
    type: WidgetType.o_card,
    visible: true,
    relatedData: {
      styles: {
        root: 'min-w-[200px] basis-1/3 rounded-3xl',
        body: 'flex flex-col gap-4 p-8',
      },
    },
    widgets: [
      {
        name: 'stewarded_value_header',
        type: WidgetType.a_text,
        visible: true,
        widgets: [
          {
            name: 'stewarded_value_header_text',
            type: WidgetType.a_text,
            visible: true,
            relatedData: {
              i18nPath: 'global.widget.totalAccountValue',
              className: 'typography-body1 text-text-primary',
            },
          },
          {
            name: 'stewarded_value_header_tooltip',
            type: WidgetType.m_tooltip,
            visible: true,
            relatedData: {},
            widgets: [
              {
                name: 'stewarded_value_header_tooltip_trigger',
                type: WidgetType.m_tooltip_trigger,
                visible: true,
                widgets: [
                  {
                    name: 'stewarded_value_header_tooltip_trigger_icon',
                    type: WidgetType.a_icon,
                    visible: true,
                    relatedData: {
                      icon: 'ri-information-line',
                      className: 'ml-1 text-active-54',
                    },
                  },
                ],
              },
              {
                name: 'stewarded_value_header_tooltip_content',
                type: WidgetType.m_tooltip_content,
                visible: true,
                widgets: [
                  {
                    name: 'stewarded_value_header',
                    type: WidgetType.a_text,
                    visible: true,
                    relatedData: {
                      text: `${t('global.widget.totalAccountValueTooltip', {
                        value: formattedStewardedValue,
                        avgEIIperHa: formattedStewardedValuePerHa,
                        totalArea: formattedAreaValue,
                      })}\n\n${t('global.analysis.preciseValue', {
                        value: formattedStewardedValue,
                      })}`,
                      className: 'whitespace-pre-wrap',
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        name: 'stewarded_value_amount',
        type: WidgetType.m_currency,
        visible: !isNullish(stewardedValueData?.value),
        relatedData: {
          value: stewardedValueData?.value,
          currency: stewardedValueData?.currency,
          className: 'typography-h2 text-text-primary',
        },
      },
      {
        name: 'stewarded_value_trend_pill',
        type: WidgetType.a_pill,
        visible: !isNullish(qtd_change),
        relatedData: {
          className: 'bg-neutral-black-4 typography-body2semibold',
        },
        widgets: [
          ...(qtd_change > 0
            ? [
                {
                  name: 'stewarded_value_trend_pill_icon',
                  type: WidgetType.a_icon,
                  visible: true,
                  relatedData: {
                    icon: 'ri-arrow-right-up-line',
                    className: 'text-success',
                  },
                },
              ]
            : []),
          ...(qtd_change < 0
            ? [
                {
                  name: 'stewarded_value_trend_pill_icon',
                  type: WidgetType.a_icon,
                  visible: true,
                  relatedData: {
                    icon: 'ri-arrow-right-down-line',
                    className: 'text-error',
                  },
                },
              ]
            : []),
          {
            name: 'stewarded_value_trend_pill_text',
            type: WidgetType.m_trend,
            visible: true,
            relatedData: {
              trend: qtd_change,
              text: 'QTD',
            },
          },
        ],
      },
    ],
  } as WidgetSlotConfiguration;
};
